import React from "react";
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  IconButton,
  Switch,
  FormControlLabel,
  Chip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { ControlNode, ValueType, TransformKind, DeclareVar, QuestionAnswerType, VariableReferenceType, NODE_METADATA } from "./types";
import VariableReferenceInput from "./VariableReferenceInput";
import { getNodeDisplayLabel } from "./utils";

interface PropertiesPanelProps {
  selectedNode: ControlNode | null;
  allNodes?: any[]; // React Flow nodes array
  onUpdateNode: (nodeId: string, updates: Partial<ControlNode>) => void;
  onDeleteNode: (nodeId: string) => void;
  readOnly?: boolean;
  availableVariables?: string[];
}

/**
 * Properties panel for editing selected node
 */
const PropertiesPanel: React.FC<PropertiesPanelProps> = ({
  selectedNode,
  allNodes = [],
  onUpdateNode,
  onDeleteNode,
  readOnly = false,
  availableVariables = [],
}) => {
  if (!selectedNode) {
    return (
      <Box sx={{ width: 340, p: 2, textAlign: "center", backgroundColor: "#F8F9FA", height: "100%" }}>
        <Box sx={{ mt: 6 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            No Selection
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Click a node to edit
          </Typography>
        </Box>
      </Box>
    );
  }

  const updateField = (field: string, value: any) => {
    onUpdateNode(selectedNode.id, { [field]: value } as Partial<ControlNode>);
  };

  return (
    <Box sx={{ width: 340, height: "100%", overflow: "auto", p: 2, backgroundColor: "#F8F9FA" }}>
      {/* Header */}
      <Box sx={{ mb: 2, p: 1.5, backgroundColor: "white", borderRadius: 1, boxShadow: 1 }}>
        <Typography variant="subtitle2" fontWeight={700} gutterBottom sx={{ fontSize: "0.9rem" }}>
          {selectedNode.node_tag.charAt(0).toUpperCase() + selectedNode.node_tag.slice(1)}
          {readOnly && <Chip label="Read Only" size="small" color="info" sx={{ ml: 1, height: 18 }} />}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace", fontSize: "0.65rem" }}>
          {selectedNode.id}
        </Typography>
      </Box>

      {/* Common Properties */}
      {!readOnly && (
        <Box sx={{ mb: 2, p: 1.5, backgroundColor: "white", borderRadius: 1, boxShadow: 1 }}>
          <TextField
            fullWidth
            label="Priority"
            type="number"
            value={selectedNode.priority}
            onChange={(e) => updateField("priority", parseFloat(e.target.value) || 0)}
            size="small"
            helperText="Higher = executes first"
            disabled={readOnly}
          />
        </Box>
      )}

      {/* Node-specific properties */}
      <Box sx={{ p: 1.5, backgroundColor: "white", borderRadius: 1, boxShadow: 1 }}>
        {renderNodeSpecificProps(selectedNode, updateField, readOnly, availableVariables, allNodes)}
      </Box>

      {!readOnly && (
        <Box sx={{ mt: 2, p: 1.5, backgroundColor: "white", borderRadius: 1, boxShadow: 1 }}>
          <Button
            fullWidth
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => onDeleteNode(selectedNode.id)}
            size="small"
          >
            Delete
          </Button>
        </Box>
      )}
    </Box>
  );
};

/**
 * Render node-specific property editors
 */
function renderNodeSpecificProps(
  node: ControlNode,
  updateField: (field: string, value: any) => void,
  readOnly: boolean,
  availableVariables: string[],
  allNodes: any[]
): React.ReactNode {
  switch (node.node_tag) {
    case "declare":
      return <DeclareNodeProps node={node as any} updateField={updateField} readOnly={readOnly} />;

    case "sequence":
    case "group":
      return <SequenceNodeProps node={node as any} updateField={updateField} readOnly={readOnly} allNodes={allNodes} />;

    case "map":
      return <MapNodeProps node={node as any} updateField={updateField} readOnly={readOnly} availableVariables={availableVariables} />;

    case "if":
      return <IfNodeProps node={node as any} updateField={updateField} readOnly={readOnly} />;

    case "share_info":
      return <ShareInfoNodeProps node={node as any} updateField={updateField} readOnly={readOnly} />;

    case "influence":
      return <InfluenceNodeProps node={node as any} updateField={updateField} readOnly={readOnly} />;

    case "analyze":
      return <AnalyzeNodeProps node={node as any} updateField={updateField} readOnly={readOnly} />;

    case "assign_action":
    case "append_action":
      return <ActionNodeProps node={node as any} updateField={updateField} readOnly={readOnly} availableVariables={availableVariables} />;

    case "question":
      return <QuestionNodeProps node={node as any} updateField={updateField} readOnly={readOnly} />;

    default:
      return null;
  }
}

// Node-specific property components

const DeclareNodeProps: React.FC<{ node: any; updateField: (f: string, v: any) => void; readOnly: boolean }> = ({
  node,
  updateField,
  readOnly,
}) => {
  const variables = node.variables || [];

  const addVariable = () => {
    updateField("variables", [
      ...variables,
      { name: `var_${variables.length}`, vtype: ValueType.STR, default: null, node_tag: "declare_var" },
    ]);
  };

  const updateVariable = (index: number, field: keyof DeclareVar, value: any) => {
    const newVars = [...variables];
    newVars[index] = { ...newVars[index], [field]: value };
    updateField("variables", newVars);
  };

  const removeVariable = (index: number) => {
    updateField(
      "variables",
      variables.filter((_: any, i: number) => i !== index)
    );
  };

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
        <Typography variant="subtitle2">Variables</Typography>
        {!readOnly && (
          <IconButton size="small" onClick={addVariable}>
            <AddIcon />
          </IconButton>
        )}
      </Box>

      {variables.map((v: DeclareVar, i: number) => (
        <Box key={i} sx={{ mb: 2, p: 1.5, border: "1px solid #E5E7EB", borderRadius: 1 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Variable {i + 1}
            </Typography>
            {!readOnly && (
              <IconButton size="small" onClick={() => removeVariable(i)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          </Box>

          <TextField
            fullWidth
            size="small"
            label="Name"
            value={v.name}
            onChange={(e) => updateVariable(i, "name", e.target.value)}
            sx={{ mb: 1 }}
            disabled={readOnly}
          />

          <FormControl fullWidth size="small" sx={{ mb: 1 }}>
            <InputLabel>Type</InputLabel>
            <Select value={v.vtype} onChange={(e) => updateVariable(i, "vtype", e.target.value)} label="Type" disabled={readOnly}>
              <MenuItem value={ValueType.STR}>String</MenuItem>
              <MenuItem value={ValueType.INT}>Integer</MenuItem>
              <MenuItem value={ValueType.FLOAT}>Float</MenuItem>
              <MenuItem value={ValueType.BOOL}>Boolean</MenuItem>
              <MenuItem value={ValueType.SET_STR}>Set[String]</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            size="small"
            label="Default Value"
            value={v.default || ""}
            onChange={(e) => updateVariable(i, "default", e.target.value)}
            disabled={readOnly}
          />
        </Box>
      ))}
    </Box>
  );
};

const SequenceNodeProps: React.FC<{
  node: any;
  updateField: (f: string, v: any) => void;
  readOnly: boolean;
  allNodes: any[];
}> = ({
  node,
  updateField,
  readOnly,
  allNodes,
}) => {
  const children = node.children || [];

  // Create a map from node ID to node data
  const nodeMap = React.useMemo(() => {
    const map = new Map<string, any>();
    allNodes.forEach(n => map.set(n.id, n.data));
    return map;
  }, [allNodes]);

  const getChildDisplayInfo = (childId: string) => {
    const childNode = nodeMap.get(childId);
    if (!childNode) {
      return { label: childId, icon: "📌", color: "#6B7280" }; // Fallback
    }
    const metadata = NODE_METADATA[childNode.node_tag as keyof typeof NODE_METADATA];
    return {
      label: getNodeDisplayLabel(childNode),
      icon: metadata?.icon || "📌",
      color: metadata?.color || "#6B7280",
    };
  };

  const moveChildUp = (index: number) => {
    if (index === 0) return;
    const newChildren = [...children];
    [newChildren[index - 1], newChildren[index]] = [newChildren[index], newChildren[index - 1]];
    updateField("children", newChildren);
  };

  const moveChildDown = (index: number) => {
    if (index === children.length - 1) return;
    const newChildren = [...children];
    [newChildren[index], newChildren[index + 1]] = [newChildren[index + 1], newChildren[index]];
    updateField("children", newChildren);
  };

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Child Nodes Order
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
        {node.node_tag === "sequence"
          ? "Children execute in this order (top to bottom)"
          : "Children can execute in any order"}
      </Typography>

      {children.length === 0 ? (
        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic" }}>
          No child nodes. Drag nodes onto this node to add children.
        </Typography>
      ) : (
        <Box>
          {children.map((childId: string, index: number) => {
            const displayInfo = getChildDisplayInfo(childId);
            return (
              <Box
                key={childId}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 0.75,
                  p: 1,
                  backgroundColor: "#F3F4F6",
                  borderRadius: 1,
                  border: "1px solid #E5E7EB",
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary", minWidth: 20 }}>
                  {index + 1}.
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flex: 1 }}>
                  <Typography sx={{ fontSize: "0.9rem", lineHeight: 1 }}>
                    {displayInfo.icon}
                  </Typography>
                  <Chip
                    label={displayInfo.label}
                    size="small"
                    sx={{
                      flex: 1,
                      fontSize: "0.7rem",
                      height: 22,
                      backgroundColor: `${displayInfo.color}15`,
                      borderLeft: `3px solid ${displayInfo.color}`,
                      "& .MuiChip-label": { px: 1 },
                    }}
                  />
                </Box>
                {!readOnly && node.node_tag === "sequence" && (
                  <Box sx={{ display: "flex", gap: 0.5 }}>
                    <IconButton
                      size="small"
                      onClick={() => moveChildUp(index)}
                      disabled={index === 0}
                      sx={{ p: 0.5 }}
                    >
                      <ArrowUpwardIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => moveChildDown(index)}
                      disabled={index === children.length - 1}
                      sx={{ p: 0.5 }}
                    >
                      <ArrowDownwardIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

const MapNodeProps: React.FC<{
  node: any;
  updateField: (f: string, v: any) => void;
  readOnly: boolean;
  availableVariables: string[];
}> = ({
  node,
  updateField,
  readOnly,
  availableVariables,
}) => (
  <>
    <Box sx={{ mb: 2 }}>
      <VariableReferenceInput
        label="Source Set Variable"
        value={node.source_set_var || ""}
        onChange={(value) => updateField("source_set_var", value)}
        availableVariables={availableVariables}
        helperText="The collection variable to loop through (must be a set[str])"
        disabled={readOnly}
      />
    </Box>
    <TextField
      fullWidth
      size="small"
      label="Iteration Variable"
      value={node.iter_var || ""}
      onChange={(e) => updateField("iter_var", e.target.value)}
      helperText="Variable name for each item (accessible in child nodes)"
      disabled={readOnly}
    />
  </>
);

const IfNodeProps: React.FC<{ node: any; updateField: (f: string, v: any) => void; readOnly: boolean }> = ({
  node,
  updateField,
  readOnly,
}) => (
  <TextField
    fullWidth
    multiline
    rows={3}
    size="small"
    label="Boolean Expression"
    value={node.expr || ""}
    onChange={(e) => updateField("expr", e.target.value)}
    sx={{ mb: 2 }}
    helperText="Python boolean expression (e.g., is_truthy(my_var))"
    disabled={readOnly}
  />
);

const ShareInfoNodeProps: React.FC<{ node: any; updateField: (f: string, v: any) => void; readOnly: boolean }> = ({
  node,
  updateField,
  readOnly,
}) => (
  <TextField
    fullWidth
    multiline
    rows={4}
    size="small"
    label="Message Template"
    value={node.template || ""}
    onChange={(e) => updateField("template", e.target.value)}
    sx={{ mb: 2 }}
    helperText="What the bot will say. Use {{variable_name}} to insert variable values"
    disabled={readOnly}
  />
);

const InfluenceNodeProps: React.FC<{ node: any; updateField: (f: string, v: any) => void; readOnly: boolean }> = ({
  node,
  updateField,
  readOnly,
}) => (
  <>
    <TextField
      fullWidth
      multiline
      rows={3}
      size="small"
      label="Guidance (Optional)"
      value={node.guidance || ""}
      onChange={(e) => updateField("guidance", e.target.value || null)}
      sx={{ mb: 2 }}
      helperText="Instructions for how the bot should behave or what to say"
      disabled={readOnly}
    />

    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1, mt: 1 }}>
      Emotional Adjustments (range: -1 to 1)
    </Typography>

    <TextField
      fullWidth
      size="small"
      type="number"
      label="Valence Delta"
      value={node.valence_delta ?? ""}
      onChange={(e) => updateField("valence_delta", e.target.value ? parseFloat(e.target.value) : null)}
      sx={{ mb: 2 }}
      inputProps={{ min: -1, max: 1, step: 0.1 }}
      helperText="Positive/negative emotion (-1 = negative, +1 = positive)"
      disabled={readOnly}
    />

    <TextField
      fullWidth
      size="small"
      type="number"
      label="Activation Delta"
      value={node.activation_delta ?? ""}
      onChange={(e) => updateField("activation_delta", e.target.value ? parseFloat(e.target.value) : null)}
      sx={{ mb: 2 }}
      inputProps={{ min: -1, max: 1, step: 0.1 }}
      helperText="Energy level (-1 = calm, +1 = energized)"
      disabled={readOnly}
    />

    <TextField
      fullWidth
      size="small"
      type="number"
      label="Engagement Delta"
      value={node.engagement_delta ?? ""}
      onChange={(e) => updateField("engagement_delta", e.target.value ? parseFloat(e.target.value) : null)}
      sx={{ mb: 2 }}
      inputProps={{ min: -1, max: 1, step: 0.1 }}
      helperText="Attention level (-1 = disengaged, +1 = engaged)"
      disabled={readOnly}
    />

    <FormControlLabel
      control={
        <Switch checked={node.ends_scenario} onChange={(e) => updateField("ends_scenario", e.target.checked)} disabled={readOnly} />
      }
      label="Ends Scenario"
      sx={{ mb: 1 }}
    />

    {node.ends_scenario && (
      <TextField
        fullWidth
        size="small"
        label="Exit Type"
        value={node.exit_type || "normal"}
        onChange={(e) => updateField("exit_type", e.target.value)}
        sx={{ mb: 2 }}
        disabled={readOnly}
      />
    )}
  </>
);

const AnalyzeNodeProps: React.FC<{ node: any; updateField: (f: string, v: any) => void; readOnly: boolean }> = ({
  node,
  updateField,
  readOnly,
}) => {
  const criteria = node.criteria || {};

  return (
    <>
      <TextField
        fullWidth
        size="small"
        label="Criteria ID"
        value={criteria.id || ""}
        onChange={(e) => updateField("criteria", { ...criteria, id: e.target.value })}
        sx={{ mb: 2 }}
        helperText="Unique identifier for this criteria"
        disabled={readOnly}
      />

      <TextField
        fullWidth
        multiline
        rows={2}
        size="small"
        label="Analyze Question"
        value={criteria.analyze_question || ""}
        onChange={(e) => updateField("criteria", { ...criteria, analyze_question: e.target.value })}
        sx={{ mb: 2 }}
        helperText="The main question to analyze in the response"
        disabled={readOnly}
      />

      <TextField
        fullWidth
        multiline
        rows={3}
        size="small"
        label="Guidance (Optional)"
        value={criteria.guidance || ""}
        onChange={(e) => updateField("criteria", { ...criteria, guidance: e.target.value || null })}
        sx={{ mb: 2 }}
        helperText="Input guidance for this criteria"
        disabled={readOnly}
      />

      <FormControl fullWidth size="small" sx={{ mb: 2 }}>
        <InputLabel>Answer Type</InputLabel>
        <Select
          value={criteria.answer_type || QuestionAnswerType.STRING}
          onChange={(e) => updateField("criteria", { ...criteria, answer_type: e.target.value })}
          label="Answer Type"
          disabled={readOnly}
        >
          <MenuItem value={QuestionAnswerType.BOOLEAN}>Boolean</MenuItem>
          <MenuItem value={QuestionAnswerType.STRING}>String</MenuItem>
          <MenuItem value={QuestionAnswerType.INTEGER}>Integer</MenuItem>
          <MenuItem value={QuestionAnswerType.FLOAT}>Float</MenuItem>
        </Select>
      </FormControl>

      <TextField
        fullWidth
        multiline
        rows={2}
        size="small"
        label="Answer Guidance (Optional)"
        value={criteria.answer_guidance || ""}
        onChange={(e) => updateField("criteria", { ...criteria, answer_guidance: e.target.value || null })}
        sx={{ mb: 2 }}
        helperText="Optional guidance on how to answer this criteria"
        disabled={readOnly}
      />

      <FormControlLabel
        control={
          <Switch
            checked={criteria.complete_required || false}
            onChange={(e) =>
              updateField("criteria", { ...criteria, complete_required: e.target.checked })
            }
            disabled={readOnly}
          />
        }
        label="Complete Required"
        sx={{ mb: 1 }}
      />

      <FormControlLabel
        control={
          <Switch
            checked={criteria.consider_asked_when_primary_is_asked || false}
            onChange={(e) =>
              updateField("criteria", {
                ...criteria,
                consider_asked_when_primary_is_asked: e.target.checked,
              })
            }
            disabled={readOnly}
          />
        }
        label="Consider Asked When Primary Is Asked"
        sx={{ mb: 2 }}
      />

      <FormControlLabel
        control={
          <Switch
            checked={node.close_on_finalize}
            onChange={(e) => updateField("close_on_finalize", e.target.checked)}
            disabled={readOnly}
          />
        }
        label="Close on Finalize"
        sx={{ mb: 1 }}
      />
    </>
  );
};

const ActionNodeProps: React.FC<{
  node: any;
  updateField: (f: string, v: any) => void;
  readOnly: boolean;
  availableVariables: string[];
}> = ({ node, updateField, readOnly, availableVariables }) => {
  // Helper to convert node_path array to scope level
  const getNodePathLevel = (nodePath: any): number => {
    if (!nodePath || !nodePath.path) return 1; // default to parent
    return nodePath.path.length; // each ".." is one level
  };

  // Helper to create node_path from scope level
  const createNodePath = (level: number): any => {
    if (level === 0) return null;
    return { path: Array(level).fill("..") };
  };

  const targetScopeLevel = getNodePathLevel(node.target?.node_path);
  const sourceScopeLevel = getNodePathLevel(node.source?.node_path);

  return (
    <>
      <Box sx={{ mb: 2 }}>
        <VariableReferenceInput
          label="Target Variable"
          value={node.target?.name || ""}
          onChange={(varName) =>
            updateField("target", {
              vtype: VariableReferenceType.LVAL,
              name: varName,
              node_path: createNodePath(targetScopeLevel),
              constant: null,
            })
          }
          availableVariables={availableVariables}
          helperText="Variable to assign the value to"
          disabled={readOnly}
          showScopeSelector={true}
          scopeLevel={targetScopeLevel}
          defaultScopeLevel={2} // Most common: grandparent (declare_root)
          onScopeChange={(level) =>
            updateField("target", {
              ...node.target,
              node_path: createNodePath(level),
            })
          }
        />
      </Box>

      <Box sx={{ mb: 2 }}>
        <VariableReferenceInput
          label="Source Variable"
          value={node.source?.name || ""}
          onChange={(varName) =>
            updateField("source", {
              vtype: VariableReferenceType.RVAL,
              name: varName,
              node_path: createNodePath(sourceScopeLevel),
              constant: null,
            })
          }
          availableVariables={availableVariables}
          helperText="Variable to read the value from (supports .status suffix)"
          disabled={readOnly}
          placeholder="e.g., injury_type or criteria.status"
          showScopeSelector={true}
          scopeLevel={sourceScopeLevel}
          defaultScopeLevel={1} // Most common: parent (question/analyze node)
          onScopeChange={(level) =>
            updateField("source", {
              ...node.source,
              node_path: createNodePath(level),
            })
          }
        />
      </Box>

      <TextField
        fullWidth
        size="small"
        label="Value Template (Optional)"
        value={node.value_template || ""}
        onChange={(e) => updateField("value_template", e.target.value || null)}
        multiline
        rows={2}
        sx={{ mb: 2 }}
        helperText="Template string for generating the value (supports {{variable}} interpolation)"
        disabled={readOnly}
      />

      <FormControl fullWidth size="small" sx={{ mb: 2 }}>
        <InputLabel>Transform</InputLabel>
        <Select
          value={node.transform || TransformKind.IDENTITY}
          onChange={(e) => updateField("transform", e.target.value)}
          label="Transform"
          disabled={readOnly}
        >
          <MenuItem value={TransformKind.IDENTITY}>Identity</MenuItem>
          <MenuItem value={TransformKind.TO_INT}>To Int</MenuItem>
          <MenuItem value={TransformKind.TO_FLOAT}>To Float</MenuItem>
          <MenuItem value={TransformKind.TO_BOOL}>To Bool</MenuItem>
          <MenuItem value={TransformKind.TO_STR}>To Str</MenuItem>
          <MenuItem value={TransformKind.EXPR}>Expression</MenuItem>
        </Select>
      </FormControl>

      {node.transform === TransformKind.EXPR && (
        <TextField
          fullWidth
          multiline
          rows={2}
          size="small"
          label="Transform Expression"
          value={node.transform_expr || ""}
          onChange={(e) => updateField("transform_expr", e.target.value)}
          sx={{ mb: 2 }}
          disabled={readOnly}
        />
      )}
    </>
  );
};

const QuestionNodeProps: React.FC<{ node: any; updateField: (f: string, v: any) => void; readOnly: boolean }> = ({
  node,
  updateField,
  readOnly,
}) => (
  <>
    <TextField
      fullWidth
      size="small"
      label="Question ID (Late Binding)"
      value={node.question_id || ""}
      onChange={(e) => updateField("question_id", e.target.value || null)}
      sx={{ mb: 2 }}
      helperText="Leave empty to define question directly"
      disabled={readOnly}
    />

    {!node.question_id && (
      <Typography variant="caption" color="text.secondary">
        Direct goal_question editing coming soon. For now, use question_id for late binding or edit the JSON
        directly.
      </Typography>
    )}
  </>
);

export default PropertiesPanel;
